import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Tooltip, Badge, Avatar, Menu, MenuItem, IconButton } from "@mui/material";
import FigmaIcon from "./icons/FigmaIcon";
import { resolvePublicAssetUrl } from "../utils/apiUrls";
import { mergeStoredUser } from "../utils/userSession";
const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const notificationAuthHeaders = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || "";
  const raw = String(token).replace(/^Bearer\s+/i, "").trim();
  return raw ? { Authorization: `Bearer ${raw}`, "x-access-token": raw } : {};
};

export default function HeaderProfile({ user, onProfileMenuOpen, mobile = false }) {
  const navigate = useNavigate();
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const loadNotifications = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/notifications?limit=10`, {
        headers: notificationAuthHeaders(), credentials: "include",
      });
      if (!response.ok) return;
      const payload = await response.json();
      setNotifications(Array.isArray(payload?.notifications) ? payload.notifications : []);
      setUnreadNotifications(Number(payload?.unread_count || 0));
    } catch { /* The header remains usable while offline. */ }
  }, []);

  useEffect(() => {
    loadNotifications();
    const refreshInterval = window.setInterval(loadNotifications, 30000);
    return () => window.clearInterval(refreshInterval);
  }, [loadNotifications]);

  const openNotifications = (event) => {
    setNotificationAnchor(event.currentTarget);
    loadNotifications();
  };
  const markAllNotificationsRead = async () => {
    setUnreadNotifications(0);
    setNotifications((current) => current.map((item) => ({ ...item, is_read: 1 })));
    try {
      await fetch(`${API_BASE}/api/notifications/read-all`, {
        method: "POST", headers: notificationAuthHeaders(), credentials: "include",
      });
    } catch { /* Optimistic UI state is refreshed next time the bell is opened. */ }
  };
  const markNotificationRead = async (notification) => {
    if (!notification?.id) return;
    const wasUnread = !Number(notification.is_read);
    setNotifications((current) => current.map((item) =>
      String(item.id) === String(notification.id) ? { ...item, is_read: 1 } : item
    ));
    if (wasUnread) setUnreadNotifications((count) => Math.max(0, count - 1));
    try {
      await fetch(`${API_BASE}/api/notifications/${encodeURIComponent(String(notification.id))}/read`, {
        method: "POST", headers: notificationAuthHeaders(), credentials: "include",
      });
    } catch { loadNotifications(); }
  };
  const openNotification = (notification) => {
    markNotificationRead(notification);
    setNotificationAnchor(null);

    const entityType = String(notification?.entity_type || "").trim().toLowerCase();
    const entityId = String(notification?.entity_id || "").trim();
    if (!entityId) return;

    switch (entityType) {
      case "job":
        navigate(`/jobs?jobId=${encodeURIComponent(entityId)}&tab=activities`);
        break;
      case "candidate":
        navigate(`/candidates?candidateId=${encodeURIComponent(entityId)}&tab=activities`);
        break;
      case "lead":
        navigate(`/leads?leadId=${encodeURIComponent(entityId)}&tab=activities`);
        break;
      case "company":
      case "client":
        navigate(`/companies?companyId=${encodeURIComponent(entityId)}&tab=activities&entityType=company&returnTab=companies`);
        break;
      case "contact":
        navigate(`/contacts/${encodeURIComponent(entityId)}`);
        break;
      default:
        break;
    }
  };

  let roleLabel = "";
  switch (user?.role) {
    case "system_user":
      roleLabel = "System";
      break;
    case "recruiter":
      roleLabel = "HR Director";
      break;
    case "admin":
    case "administrator":
      roleLabel = "Administrator";
      break;
    case "candidate":
      roleLabel = "Job Seeker";
      break;
    default:
      roleLabel = user?.role || "User";
      break;
  }

  const [avatarError, setAvatarError] = useState(false);
  const [avatarBust, setAvatarBust] = useState(Date.now());

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const rawProfileImage = user?.profile_image || storedUser?.profile_image || "";

  useEffect(() => {
    setAvatarError(false);
    setAvatarBust(Date.now());
  }, [rawProfileImage]);

  const avatarSrc = useMemo(() => {
    if (!rawProfileImage) return "/default-avatar.png";
    return (
      resolvePublicAssetUrl(rawProfileImage, {
        apiBase: API_BASE,
        preferApiProxyForUploads: true,
        cacheBust: avatarBust,
      }) || "/default-avatar.png"
    );
  }, [rawProfileImage, avatarBust]);

  const goToMyUserView = useMemo(() => {
    return () => {
      const userId = user?.id || user?.user_id;
      if (!userId) return;

      navigate(`/users/${encodeURIComponent(String(userId))}`, {
        state: { recruiter: user },
      });
    };
  }, [navigate, user]);

  return (
          <Box
            className="profile-menu-container"
            sx={{
              display: "flex !important",
              alignItems: "center",
              flexWrap: "nowrap !important",
              ...(mobile ? {
                bgcolor: "transparent",
                borderRadius: 1,
                px: 0.5,
                py: 0.25,
                "& .notification-icon, & .profile-name, & .profile-role-label": { color: "#fff" },
                "& .topbar-profile-avatar": { bgcolor: "transparent", borderColor: "transparent" },
                "& .topbar-profile-trigger": { gap: "6px" },
                "& .topbar-profile-divider": { display: "none" },
                "& .topbar-profile-copy": { maxWidth: "100px" },
              } : {}),
            }}
          >
            <Box className="item topbar-notification-item" onMouseEnter={openNotifications}>
              <Tooltip title="View notifications">
                <Badge
                  overlap="circular"
                  variant="dot"
                  invisible={!unreadNotifications}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#12B76A",
                      boxShadow: "0 0 0 2px #fff",
                      minWidth: 10,
                      width: 10,
                      height: 10,
                      borderRadius: "999px",
                      top: 4,
                      right: 4,
                    },
                  }}
                >
                  <IconButton aria-label="Notifications" onClick={openNotifications} sx={{ color: "#667085" }}><FigmaIcon name="notification" className="notification-icon" color="#9C4FFF" /></IconButton>
                </Badge>
              </Tooltip>
            </Box>
            <Menu
              anchorEl={notificationAnchor}
              open={Boolean(notificationAnchor)}
              onClose={() => setNotificationAnchor(null)}
              MenuListProps={{ onMouseLeave: () => setNotificationAnchor(null) }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { width: 360, maxWidth: "calc(100vw - 24px)", mt: 1, borderRadius: 2 } }}
            >
              <MenuItem disabled sx={{ opacity: "1 !important", py: 1.1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#101828" }}>Notifications</Typography>
              </MenuItem>
              <MenuItem onClick={markAllNotificationsRead} disabled={!unreadNotifications}>Mark all as read</MenuItem>
              {notifications.length ? notifications.map((item) => (
                <MenuItem key={item.id} onClick={() => openNotification(item)} sx={{ alignItems: "flex-start", gap: 1, whiteSpace: "normal", py: 1.2, backgroundColor: Number(item.is_read) ? "transparent" : "#F9F5FF" }}>
                  <Avatar
                    src={resolvePublicAssetUrl(item.actor_profile_image, { apiBase: API_BASE, preferApiProxyForUploads: true }) || undefined}
                    sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, bgcolor: "#F5F3FF", color: "#9C4FFF" }}
                  >
                    {String(item.title || "A").trim().charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}><Typography sx={{ fontSize: 13, fontWeight: 600, color: "#344054" }}>{item.title}</Typography><Typography sx={{ mt: 0.35, fontSize: 12, color: "#667085", whiteSpace: "pre-wrap" }}>{item.body}</Typography></Box>
                </MenuItem>
              )) : <MenuItem disabled><Typography sx={{ fontSize: 13 }}>You have no notifications.</Typography></MenuItem>}
            </Menu>

            <Box className="topbar-profile-divider" aria-hidden="true" />

            <Box className="item topbar-profile-item">
              {user && (
                <Box className="topbar-profile-trigger" onClick={onProfileMenuOpen}>
                  <Box className="topbar-profile-avatar-shell">
                    <Avatar
                      src={avatarError ? "/default-avatar.png" : avatarSrc}
                      imgProps={{
                        onError: () => {
                          setAvatarError(true);

                          if (rawProfileImage && /\/uploads\//i.test(rawProfileImage)) {
                            mergeStoredUser({ profile_image: "" });
                          }
                        },
                      }}
                      className="topbar-profile-avatar"
                      sx={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        goToMyUserView();
                      }}
                    />
                    <Box className="topbar-profile-status-dot" aria-hidden="true" />
                  </Box>

                  <Box className="topbar-profile-copy">
                    <Typography className="profile-name" variant="body1">
                      {user.name || user.full_name || "User"}
                    </Typography>
                    <Typography className="profile-role-label" component="p">
                      {roleLabel}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
  );
}
